import {
  useFormatToCurrencyCutoff,
  useFormatToSoftDecimal,
  useLocale,
} from '@dreamstack/i18n'
import type { BlockRibbon } from '@dreamstack/payload/src/payload-types'
import { useOnScreen } from '@dreamstack/util'
import { clsxMerge, cn } from '@dreamstack/util/src/cn'
import { motion } from 'framer-motion'
import filter from 'lodash/filter'
import map from 'lodash/map'
import shuffle from 'lodash/shuffle'
import type { FunctionComponent } from 'react'
import { useMemo, useRef } from 'react'
import { CountUp } from 'use-count-up'
import {
  BUBBLES_COUNTER_DEFAULT_DURATION,
  bubbleConfigs,
  parseHeadingString,
  useAnimateControls,
} from './BubblesRibbon'

type BubbleContainerProps = {
  backgroundColor: string
  children: React.ReactNode
}
const BubbleContainer: React.FC<BubbleContainerProps> = ({
  backgroundColor,
  children,
}) => (
  <motion.div
    style={{ backgroundColor }}
    className={cn(
      'flex h-[150px] w-[150px] sm:h-[300px] sm:w-[300px] md:h-[200px] md:w-[200px] ml:h-[300px] ml:w-[300px] rounded-full -m-px-12 sm:-m-px-16 md:-m-px-12 ml:-m-px-16 text-center items-center justify-center'
    )}
  >
    {children}
  </motion.div>
)

type BigFontProps = {
  darkBackground?: boolean
  animateControls: any
  children: React.ReactNode
}
const BigFont: React.FC<BigFontProps> = ({
  darkBackground,
  animateControls,
  children,
}) => (
  <motion.p
    animate={animateControls}
    className={clsxMerge(
      darkBackground ? 'text-accentroWhite-full' : 'text-accentroAqua-full',
      'flex flex-col text-px-16 sm:text-px-28 md:text-px-20 ml:text-px-28 font-semibold tracking-tight'
    )}
  >
    {children}
  </motion.p>
)

type SmallFontProps = { darkBackground?: boolean; subheading?: string }
const SmallFont: React.FC<SmallFontProps> = ({
  darkBackground,
  subheading,
}) => (
  <p
    className={clsxMerge(
      darkBackground && 'text-accentroWhite-full',
      'text-px-13 sm:text-px-16 md:text-px-13 ml:text-px-16'
    )}
    dangerouslySetInnerHTML={subheading ? { __html: subheading } : undefined}
  />
)

//
const Bubble: FunctionComponent<
  React.PropsWithChildren<{
    bigFontTeal?: boolean
    smallFontGray?: boolean
    count?: number | null
    subheading?: string
    backgroundColor: string
    surroundingString?: string
    onScreen: boolean
    animationDuration: number
  }>
> = ({
  bigFontTeal,
  smallFontGray,
  count,
  subheading,
  backgroundColor,
  surroundingString,
  onScreen,
  animationDuration = BUBBLES_COUNTER_DEFAULT_DURATION,
}) => {
  const decimalFormat = useFormatToSoftDecimal({
    maxFractionDigits: 1,
    minFractionDigits: 1,
  })
  const cutoffFormat = useFormatToCurrencyCutoff()
  const format =
    typeof count === 'number' && count % 1 === 0 ? cutoffFormat : decimalFormat

  // Keep separator while splitting, so we can determine pre and post based on array position of the seperator.
  const splitted = surroundingString?.split(/(\$)/g)
  const indexOfCount = splitted?.indexOf('$')
  const preText = indexOfCount ? splitted?.[indexOfCount - 1] : null
  const postText = indexOfCount ? splitted?.[indexOfCount + 1] : null

  const duration = animationDuration // random(1.2, 1.8, true)

  const controls = useAnimateControls({ duration, onScreen })

  return (
    <BubbleContainer backgroundColor={backgroundColor}>
      <div>
        <BigFont animateControls={controls} darkBackground={!bigFontTeal}>
          {preText}
          {!!count && !isNaN(count) && (
            <CountUp
              duration={duration}
              end={count}
              isCounting={onScreen}
              formatter={format}
            />
          )}
          {postText}
        </BigFont>
        <SmallFont darkBackground={!smallFontGray} subheading={subheading} />
      </div>
    </BubbleContainer>
  )
}

//TODO: FIXME: This component looks like copy pasta from Bubbles Ribbon. Merge both files.
export const BubblesSmallFontRibbon: FunctionComponent<
  React.PropsWithChildren<{
    block: BlockRibbon
  }>
> = ({ block }) => {
  const declaredBubbles = useMemo(() => {
    return filter(
      [
        { heading: block.heading1, subheading: block.subheading1 },
        { heading: block.heading2, subheading: block.subheading2 },
        { heading: block.heading3, subheading: block.subheading3 },
        { heading: block.heading4, subheading: block.subheading4 },
      ],
      (bubble) => !!bubble.heading
    )
  }, [
    block.heading1,
    block.heading2,
    block.heading3,
    block.heading4,
    block.subheading1,
    block.subheading2,
    block.subheading3,
    block.subheading4,
  ])

  const locale = useLocale()
  const ref = useRef<HTMLDivElement | null>(null)
  const onScreen = useOnScreen(ref)
  const initialDuration = 1.2
  const durationPresets = shuffle(
    map(declaredBubbles, (_, index) => {
      return initialDuration + index * 0.2
    })
  )

  return (
    <div className="flex justify-center">
      <div className="grid grid-cols-2 md:grid-cols-4 m-auto" ref={ref}>
        {map(declaredBubbles, (bubble, index) => {
          const drawDuration = durationPresets[index]
          const config = {
            ...bubbleConfigs[index],
            ...parseHeadingString(locale, bubble.heading ?? undefined),
          }

          return (
            <Bubble
              onScreen={onScreen}
              key={index}
              {...config}
              subheading={bubble.subheading ?? undefined}
              animationDuration={drawDuration}
            />
          )
        })}
      </div>
    </div>
  )
}
